import { MentionType } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { isKnownSystemEvent } from './system-event-presentation';

/**
 * The one place a marker name becomes words, for the thread card and the Inbox
 * preview alike. Keeping every `t()` call here also keeps the literal
 * `messages.systemEvent.` prefix visible to the i18n orphan checker.
 */
export const useSystemEventLabel = () => {
  const { t, i18n } = useTranslation();

  const systemEventTitle = (event: string, values?: Record<string, string>): string =>
    isKnownSystemEvent(event)
      ? t(`messages.systemEvent.${event}`, values)
      : t('messages.systemEvent.unknown');

  /**
   * A marker that names what it is about in its title: its collab, or the
   * product of a gift. The name is denormalized at write time; a marker without
   * one (a historical collab marker, a gift offering several products) takes
   * the nameless wording rather than showing a raw template or `undefined`.
   */
  const namedMarkerTitle = (
    event: string,
    variable: 'collabName' | 'productTitle',
    name: string | null | undefined
  ): string => {
    if (!isKnownSystemEvent(event)) return t('messages.systemEvent.unknown');

    return name
      ? t(`messages.systemEvent.${event}`, { [variable]: name })
      : t(`messages.systemEvent.${event}_unnamed`);
  };

  const collabMarkerTitle = (event: string, collabName: string | undefined): string =>
    namedMarkerTitle(event, 'collabName', collabName);

  const giftMarkerTitle = (event: string, productTitle: string | null | undefined): string =>
    namedMarkerTitle(event, 'productTitle', productTitle);

  const mentionTitle = (mentionType: MentionType | undefined): string => {
    if (mentionType === MentionType.STORY) return t('messages.mentionEvent.story');
    if (mentionType === MentionType.CAPTION) return t('messages.mentionEvent.caption');
    return t('messages.systemEvent.mention_received');
  };

  /** The question Fluenceur asked the creator about a UGC product, read by the brand. */
  const receptionQuestionTitle = (productName: string): string =>
    t('messages.systemEvent.product_reception_question', { productName });

  /** The day the creator's silence counted as the reception of a UGC product. */
  const receptionCountedTitle = (receivedAt: string, productName: string): string =>
    t('messages.systemEvent.product_reception_counted', {
      date: new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }).format(
        new Date(receivedAt)
      ),
      productName,
    });

  /** What the creator answered, or that she has not yet. */
  const receptionAnswerLabel = (
    answer: 'yes' | 'not_yet' | null,
    closedBy: 'creator' | 'shopify' | 'default' | null = null
  ): string => {
    if (answer === 'yes') return t('messages.receptionAnswer.yes');
    // Closed without her answer: the parcel was delivered, or the silence counted.
    if (closedBy === 'shopify') return t('messages.receptionAnswer.delivered');
    if (closedBy === 'default') return t('messages.receptionAnswer.assumed');
    if (answer === 'not_yet') return t('messages.receptionAnswer.notYet');
    return t('messages.receptionAnswer.none');
  };

  return {
    collabMarkerTitle,
    giftMarkerTitle,
    systemEventTitle,
    mentionTitle,
    receptionQuestionTitle,
    receptionCountedTitle,
    receptionAnswerLabel,
  };
};
